import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'

import type { FilterFacetItem } from './FilterSection'
import FilterSections, { type FilterSectionData } from './FilterSections'

import styles from './MultiSectionFiltersFacetsWidget.module.scss'

// Helper function to sort filter/facet data alphabetically by label
const sortFilterSectionData = <T,>(
  data: FilterSectionData<T> | undefined,
): FilterSectionData<T> | undefined => {
  if (!data) return undefined

  const sorted: FilterSectionData<T> = {}
  Object.entries(data).forEach(([sectionKey, items]) => {
    sorted[sectionKey] = [...items].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
  })
  return sorted
}

export interface MultiSectionFiltersFacetsWidgetProps<
  TFilterValue = any,
  TFacetValue = any,
> {
  enableDeselectAllButton?: boolean | Record<string, boolean>
  enableSelectAllButton?: Record<string, boolean>
  facets?: FilterSectionData<TFacetValue>
  filters?: FilterSectionData<TFilterValue>
  onDeselectSection?: (
    sectionKey: string,
    sectionType: 'filter' | 'facet',
  ) => void
  onFacetToggle?: (
    sectionKey: string,
    itemId: string | number,
    enabled: boolean,
  ) => void
  onFilterToggle?: (
    sectionKey: string,
    itemId: string | number,
    enabled: boolean,
  ) => void
  onSelectSection?: (
    sectionKey: string,
    sectionType: 'filter' | 'facet',
  ) => void
  resultsCount?: number
  sectionTitles?: Record<string, string>
  title?: string
}

const MultiSectionFiltersFacetsWidget = <
  TFilterValue = any,
  TFacetValue = any,
>({
  title = 'Filter Results',
  resultsCount = 0,
  filters,
  facets,
  enableDeselectAllButton = false,
  enableSelectAllButton,
  onFilterToggle,
  onFacetToggle,
  onDeselectSection,
  onSelectSection,
  sectionTitles,
}: MultiSectionFiltersFacetsWidgetProps<TFilterValue, TFacetValue>) => {
  const handleFilterToggle = useCallback(
    (sectionKey: string, itemId: string | number, currentEnabled: boolean) => {
      onFilterToggle?.(sectionKey, itemId, !currentEnabled)
    },
    [onFilterToggle],
  )

  const handleFacetToggle = useCallback(
    (sectionKey: string, itemId: string | number, currentEnabled: boolean) => {
      onFacetToggle?.(sectionKey, itemId, !currentEnabled)
    },
    [onFacetToggle],
  )

  const handleDeselectSection = useCallback(
    (sectionKey: string, sectionType: 'filter' | 'facet') => {
      onDeselectSection?.(sectionKey, sectionType)
    },
    [onDeselectSection],
  )

  const handleSelectSection = useCallback(
    (sectionKey: string, sectionType: 'filter' | 'facet') => {
      onSelectSection?.(sectionKey, sectionType)
    },
    [onSelectSection],
  )

  // Sort facets and filters alphabetically by label
  const sortedFacets = useMemo(() => sortFilterSectionData(facets), [facets])
  const sortedFilters = useMemo(() => sortFilterSectionData(filters), [filters])

  return (
    <div className={classNames(styles.root)}>
      <h3 className={styles.mainTitle}>
        {title} ({resultsCount})
      </h3>
      <FilterSections
        enableDeselectAllButton={enableDeselectAllButton}
        enableSelectAllButton={enableSelectAllButton}
        facets={sortedFacets}
        filters={sortedFilters}
        sectionTitles={sectionTitles}
        onDeselectSection={handleDeselectSection}
        onFacetToggle={handleFacetToggle}
        onFilterToggle={handleFilterToggle}
        onSelectSection={handleSelectSection}
      />
    </div>
  )
}

export default MultiSectionFiltersFacetsWidget

// Re-export types for convenience
export type { FilterFacetItem, FilterSectionData }
